import {
  EXPRESSION_FUNCTION_CATALOG,
  parseContract,
  SCALAR_FUNCTIONS,
  SemanticModelSchema,
  type Aggregation,
  type Expression,
  type FieldFormat,
  type LogicalDataType,
  type ScalarFunction,
  type SemanticModel,
} from '@aibi/contracts';
import { AppError, err, ok, type ErrorDetail, type Result } from '@aibi/shared';
import { findDependencyCycles } from './dependency-cycles.js';
import { collectExpressionReferences } from './expression-references.js';
import { compatibleTypes, inferExpressionType, typeFamily } from './expression-types.js';
import { isCalculatedDimension, isCalculatedMeasure, SemanticModelIndex } from './model-index.js';

const NUMERIC_TYPES: ReadonlySet<LogicalDataType> = new Set(['integer', 'decimal']);
const ORDERABLE_TYPES: ReadonlySet<LogicalDataType> = new Set([
  'integer',
  'decimal',
  'date',
  'datetime',
  'time',
  'duration',
  'string',
]);
const TEMPORAL_TYPES: ReadonlySet<LogicalDataType> = new Set(['date', 'datetime']);

/** Tipos de columna que admite cada agregacion (`null` = cualquiera). */
const AGGREGATION_INPUT_TYPES: Readonly<Record<Aggregation, ReadonlySet<LogicalDataType> | null>> =
  {
    sum: NUMERIC_TYPES,
    avg: NUMERIC_TYPES,
    median: NUMERIC_TYPES,
    min: ORDERABLE_TYPES,
    max: ORDERABLE_TYPES,
    count: null,
    'count-distinct': null,
  };

const BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(SCALAR_FUNCTIONS);
const isBuiltinFunction = (name: string): name is ScalarFunction => BUILTIN_FUNCTIONS.has(name);

interface ExpressionRules {
  readonly allowAggregate: boolean;
  readonly allowMeasures: boolean;
  /** Tipo logico esperado del resultado (filtros: `boolean`; calculadas: su `dataType`). */
  readonly expectedType?: LogicalDataType;
}
const ROW_LEVEL: ExpressionRules = { allowAggregate: false, allowMeasures: false };
const ROW_FILTER: ExpressionRules = { ...ROW_LEVEL, expectedType: 'boolean' };
const MEASURE_LEVEL: ExpressionRules = { allowAggregate: true, allowMeasures: true };

/**
 * Valida estructura (contrato) e integridad referencial de un Semantic Model:
 * ids unicos en todo el modelo, referencias existentes, expresiones fila a fila sin
 * agregaciones, agregaciones compatibles con el tipo, aridad de funciones y ausencia
 * de dependencias circulares entre medidas calculadas.
 *
 * @example
 * const result = validateSemanticModel(JSON.parse(raw));
 * if (!result.ok) showErrors(result.error.details);
 */
export function validateSemanticModel(input: unknown): Result<SemanticModel, AppError> {
  const parsed = parseContract(SemanticModelSchema, input, 'contrato Semantic Model');
  if (!parsed.ok) return parsed;

  const issues = findIntegrityIssues(parsed.value);
  return issues.length === 0
    ? ok(parsed.value)
    : err(
        new AppError('VALIDATION_ERROR', 'El Semantic Model tiene referencias inconsistentes.', {
          details: issues,
        }),
      );
}

/** Devuelve los problemas de integridad de un modelo estructuralmente valido. */
export function findIntegrityIssues(model: SemanticModel): ErrorDetail[] {
  const issues: ErrorDetail[] = [];
  const report = (path: string, message: string): void => {
    issues.push({ path, message });
  };
  const index = new SemanticModelIndex(model);

  // Ids unicos en todo el modelo
  const allIds = collectObjectIds(model);
  const seen = new Set<string>();
  for (const { id, path } of allIds) {
    if (seen.has(id)) report(`${path}.id`, `Id duplicado en el modelo: "${id}".`);
    seen.add(id);
  }
  const objectIds = new Set(allIds.map((entry) => entry.id));

  const checkFormat = (format: FieldFormat | undefined, path: string): void => {
    if (format?.type === 'ref' && !index.getFormat(format.formatId)) {
      report(`${path}.formatId`, `Formato inexistente "${format.formatId}".`);
    }
  };
  const checkTable = (tableId: string, path: string): void => {
    if (!index.getTable(tableId)) report(path, `Tabla inexistente "${tableId}".`);
  };
  const checkExpression = (expression: Expression, path: string, rules: ExpressionRules): void => {
    const refs = collectExpressionReferences(expression);
    for (const columnId of refs.columnIds) {
      if (!index.getColumn(columnId)) report(path, `Columna inexistente "${columnId}".`);
    }
    for (const measureId of refs.measureIds) {
      if (!rules.allowMeasures) report(path, `No se permite referenciar medidas ("${measureId}").`);
      else if (!index.getMeasure(measureId)) report(path, `Medida inexistente "${measureId}".`);
    }
    if (refs.containsAggregate && !rules.allowAggregate) {
      report(path, 'No se permiten agregaciones en expresiones fila a fila.');
    }
    for (const call of refs.functionCalls) {
      if (!isBuiltinFunction(call.name)) continue;
      const { minArgs, maxArgs } = EXPRESSION_FUNCTION_CATALOG[call.name];
      if (call.argCount < minArgs || (maxArgs !== null && call.argCount > maxArgs)) {
        report(path, `La funcion "${call.name}" no admite ${call.argCount} argumento(s).`);
      }
    }
    const inferred = inferExpressionType(expression, index, (message) => {
      report(path, message);
    });
    const expected = rules.expectedType;
    if (expected !== undefined && !compatibleTypes(inferred, expected)) {
      report(path, `La expresion devuelve ${inferred} pero se declara ${expected}.`);
    }
  };

  // Tablas y columnas
  model.tables.forEach((table, t) => {
    table.columns.forEach((column, c) => {
      const path = `tables.${t}.columns.${c}`;
      if (column.sortByColumnId !== undefined && !index.getColumn(column.sortByColumnId)) {
        report(`${path}.sortByColumnId`, `Columna inexistente "${column.sortByColumnId}".`);
      }
      checkFormat(column.format, `${path}.format`);
    });
  });
  model.calculatedColumns.forEach((column, c) => {
    const path = `calculatedColumns.${c}`;
    checkTable(column.tableId, `${path}.tableId`);
    checkExpression(column.expression, `${path}.expression`, {
      ...ROW_LEVEL,
      expectedType: column.dataType,
    });
    checkFormat(column.format, `${path}.format`);
  });
  for (const cycle of findCalculatedColumnCycles(model)) {
    report('calculatedColumns', `Dependencia circular entre columnas: ${cycle.join(' -> ')}.`);
  }

  // Dimensiones
  const checkHierarchyMembership = (
    dimension: { id: string; hierarchyId?: string | undefined },
    path: string,
  ): void => {
    if (dimension.hierarchyId === undefined) return;
    const hierarchy = index.getHierarchy(dimension.hierarchyId);
    if (!hierarchy) report(path, `Jerarquia inexistente "${dimension.hierarchyId}".`);
    else if (!hierarchy.levels.some((level) => level.dimensionId === dimension.id)) {
      report(path, `La jerarquia "${hierarchy.id}" no incluye la dimension "${dimension.id}".`);
    }
  };
  model.dimensions.forEach((dimension, d) => {
    const path = `dimensions.${d}`;
    checkHierarchyMembership(dimension, `${path}.hierarchyId`);
    const resolved = index.getColumn(dimension.columnId);
    if (!resolved) report(`${path}.columnId`, `Columna inexistente "${dimension.columnId}".`);
    const dataType = resolved?.column.dataType;
    if (
      dataType !== undefined &&
      (dimension.role === 'temporal' || dimension.defaultTimeGrain !== undefined) &&
      !TEMPORAL_TYPES.has(dataType)
    ) {
      report(`${path}.role`, `Una dimension temporal requiere una columna de fecha (${dataType}).`);
    }
    checkFormat(dimension.format, `${path}.format`);
  });
  model.calculatedDimensions.forEach((dimension, d) => {
    const path = `calculatedDimensions.${d}`;
    checkTable(dimension.tableId, `${path}.tableId`);
    checkExpression(dimension.expression, `${path}.expression`, {
      ...ROW_LEVEL,
      expectedType: dimension.dataType,
    });
    checkHierarchyMembership(dimension, `${path}.hierarchyId`);
    checkFormat(dimension.format, `${path}.format`);
  });

  // Medidas
  model.measures.forEach((measure, m) => {
    const path = `measures.${m}`;
    if (measure.tableId !== undefined) checkTable(measure.tableId, `${path}.tableId`);
    if (measure.columnId === undefined) {
      if (measure.aggregation !== 'count') {
        report(`${path}.columnId`, `La agregacion "${measure.aggregation}" requiere una columna.`);
      } else if (measure.tableId === undefined) {
        report(`${path}.tableId`, 'Un conteo de filas requiere la tabla hogar (tableId).');
      }
    } else {
      const resolved = index.getColumn(measure.columnId);
      const allowed = AGGREGATION_INPUT_TYPES[measure.aggregation];
      if (!resolved) report(`${path}.columnId`, `Columna inexistente "${measure.columnId}".`);
      else if (allowed !== null && !allowed.has(resolved.column.dataType)) {
        report(
          `${path}.aggregation`,
          `"${measure.aggregation}" no es compatible con columnas ${resolved.column.dataType}.`,
        );
      }
    }
    if (measure.filter) checkExpression(measure.filter, `${path}.filter`, ROW_FILTER);
    checkFormat(measure.format, `${path}.format`);
  });
  model.calculatedMeasures.forEach((measure, m) => {
    const path = `calculatedMeasures.${m}`;
    if (measure.tableId !== undefined) checkTable(measure.tableId, `${path}.tableId`);
    checkExpression(measure.expression, `${path}.expression`, MEASURE_LEVEL);
    const refs = collectExpressionReferences(measure.expression);
    if (!refs.containsAggregate && refs.measureIds.length === 0) {
      report(`${path}.expression`, 'Una medida debe agregar datos o componerse de otras medidas.');
    }
    checkFormat(measure.format, `${path}.format`);
  });
  for (const cycle of findMeasureCycles(model)) {
    report('calculatedMeasures', `Dependencia circular entre medidas: ${cycle.join(' -> ')}.`);
  }

  // Relaciones
  const relationshipPairs = new Map<string, string>();
  model.relationships.forEach((relationship, r) => {
    const path = `relationships.${r}`;
    const from = index.getColumn(relationship.fromColumnId);
    const to = index.getColumn(relationship.toColumnId);
    if (!from)
      report(`${path}.fromColumnId`, `Columna inexistente "${relationship.fromColumnId}".`);
    if (!to) report(`${path}.toColumnId`, `Columna inexistente "${relationship.toColumnId}".`);
    if (from && from.table.id === to?.table.id) {
      report(path, 'Una relacion debe unir dos tablas distintas.');
    }
    if (from && to && !compatibleTypes(from.column.dataType, to.column.dataType)) {
      report(
        path,
        `Tipos incompatibles en la relacion: ${from.column.dataType} y ${to.column.dataType}.`,
      );
    }
    if (from && to && typeFamily(from.column.dataType) === 'other') {
      report(path, `El tipo ${from.column.dataType} no admite relaciones.`);
    }
    const pair = [relationship.fromColumnId, relationship.toColumnId].sort().join('|');
    const previous = relationshipPairs.get(pair);
    if (previous !== undefined) {
      report(path, `Relacion duplicada: une las mismas columnas que "${previous}".`);
    } else relationshipPairs.set(pair, relationship.id);
  });

  // Jerarquias y KPIs
  model.hierarchies.forEach((hierarchy, h) => {
    const seenDimensions = new Set<string>();
    const lastRank = new Map<string, number>();
    hierarchy.levels.forEach((level, l) => {
      const path = `hierarchies.${h}.levels.${l}.dimensionId`;
      if (!index.getDimension(level.dimensionId)) {
        report(path, `Dimension inexistente "${level.dimensionId}".`);
        return;
      }
      if (seenDimensions.has(level.dimensionId)) {
        report(path, `La dimension "${level.dimensionId}" se repite en la jerarquia.`);
      }
      seenDimensions.add(level.dimensionId);
      const rank = levelRank(index, level.dimensionId);
      if (!rank) return;
      const previous = lastRank.get(rank.family);
      if (previous !== undefined && rank.rank <= previous) {
        report(
          path,
          'Los niveles deben ir de mayor a menor granularidad (p. ej. anio > mes > dia, pais > estado > ciudad).',
        );
      }
      lastRank.set(rank.family, rank.rank);
    });
  });
  model.kpis.forEach((kpi, k) => {
    if (!index.getMeasure(kpi.measureId)) {
      report(`kpis.${k}.measureId`, `Medida inexistente "${kpi.measureId}".`);
    }
    if (kpi.target?.kind === 'measure' && !index.getMeasure(kpi.target.measureId)) {
      report(`kpis.${k}.target.measureId`, `Medida inexistente "${kpi.target.measureId}".`);
    }
    checkFormat(kpi.format, `kpis.${k}.format`);
  });

  // Reglas de negocio
  model.businessRules.forEach((rule, b) => {
    const path = `businessRules.${b}`;
    const { kind, targetId } = rule.appliesTo;
    if (kind !== 'model') {
      const exists =
        targetId !== undefined &&
        (kind === 'table'
          ? index.getTable(targetId) !== undefined
          : kind === 'column'
            ? index.getColumn(targetId) !== undefined
            : kind === 'dimension'
              ? index.getDimension(targetId) !== undefined
              : index.getMeasure(targetId) !== undefined);
      if (!exists)
        report(`${path}.appliesTo`, `Objeto "${kind}" inexistente: "${targetId ?? ''}".`);
    }
    checkExpression(rule.rule.expression, `${path}.rule.expression`, ROW_FILTER);
  });

  // Diccionario de datos
  model.dataDictionary.forEach((entry, e) => {
    entry.relatedObjectIds.forEach((id, i) => {
      if (!objectIds.has(id)) {
        report(`dataDictionary.${e}.relatedObjectIds.${i}`, `Objeto inexistente "${id}".`);
      }
    });
  });

  // Seguridad
  model.security.roles.forEach((role, r) => {
    role.rowFilters.forEach((filter, f) => {
      const path = `security.roles.${r}.rowFilters.${f}`;
      checkTable(filter.tableId, `${path}.tableId`);
      checkExpression(filter.expression, `${path}.expression`, ROW_FILTER);
    });
    role.objectPermissions.forEach((permission, p) => {
      if (!objectIds.has(permission.objectId)) {
        report(
          `security.roles.${r}.objectPermissions.${p}.objectId`,
          `Objeto inexistente "${permission.objectId}".`,
        );
      }
    });
  });

  // Ajustes y linaje interno
  const { primaryDateDimensionId, defaultFormatId } = model.settings;
  if (primaryDateDimensionId !== undefined) {
    const resolved = index.resolveDimension(primaryDateDimensionId);
    if (!resolved) {
      report(
        'settings.primaryDateDimensionId',
        `Dimension inexistente "${primaryDateDimensionId}".`,
      );
    } else if (!TEMPORAL_TYPES.has(resolved.dataType)) {
      report(
        'settings.primaryDateDimensionId',
        'La fecha principal debe ser una dimension de fecha.',
      );
    }
  }
  if (defaultFormatId !== undefined && !index.getFormat(defaultFormatId)) {
    report('settings.defaultFormatId', `Formato inexistente "${defaultFormatId}".`);
  }
  const datasetTableIds = new Set(model.tables.map((table) => table.datasetTableId));
  model.sources.forEach((source, s) => {
    source.datasetTableIds.forEach((id, i) => {
      if (!datasetTableIds.has(id)) {
        report(`sources.${s}.datasetTableIds.${i}`, `Ninguna tabla del modelo usa "${id}".`);
      }
    });
  });

  return issues;
}

/** Enumera todos los objetos identificables del modelo con su ruta. */
function collectObjectIds(model: SemanticModel): { id: string; path: string }[] {
  const entries: { id: string; path: string }[] = [];
  const add = (items: readonly { id: string }[], path: string): void => {
    items.forEach((item, i) => entries.push({ id: item.id, path: `${path}.${i}` }));
  };
  add(model.tables, 'tables');
  model.tables.forEach((table, t) => {
    add(table.columns, `tables.${t}.columns`);
  });
  add(model.calculatedColumns, 'calculatedColumns');
  add(model.dimensions, 'dimensions');
  add(model.calculatedDimensions, 'calculatedDimensions');
  add(model.measures, 'measures');
  add(model.calculatedMeasures, 'calculatedMeasures');
  add(model.relationships, 'relationships');
  add(model.hierarchies, 'hierarchies');
  model.hierarchies.forEach((hierarchy, h) => {
    add(hierarchy.levels, `hierarchies.${h}.levels`);
  });
  add(model.kpis, 'kpis');
  add(model.formats, 'formats');
  add(model.businessRules, 'businessRules');
  add(model.dataDictionary, 'dataDictionary');
  add(model.security.roles, 'security.roles');
  model.security.roles.forEach((role, r) => {
    add(role.rowFilters, `security.roles.${r}.rowFilters`);
  });
  return entries;
}

/** Detecta ciclos en el grafo de dependencias entre medidas calculadas. */
export function findMeasureCycles(model: SemanticModel): string[][] {
  return findDependencyCycles(
    new Map(
      [...model.measures, ...model.calculatedMeasures].map((measure) => [
        measure.id,
        isCalculatedMeasure(measure)
          ? collectExpressionReferences(measure.expression).measureIds
          : [],
      ]),
    ),
  );
}

/** Detecta ciclos entre columnas calculadas que se referencian entre si. */
export function findCalculatedColumnCycles(model: SemanticModel): string[][] {
  return findDependencyCycles(
    new Map(
      model.calculatedColumns.map((column) => [
        column.id,
        collectExpressionReferences(column.expression).columnIds,
      ]),
    ),
  );
}

const CALENDAR_RANK: Readonly<Partial<Record<string, number>>> = {
  year: 0,
  quarter: 1,
  month: 2,
  week: 3,
  day: 4,
  date: 5,
};
const GEO_RANK: Readonly<Partial<Record<string, number>>> = {
  'geo.country': 0,
  'geo.region': 1,
  'geo.state': 2,
  'geo.city': 3,
  'geo.postal-code': 4,
};

/** Nivel de granularidad de una dimension (calendario o geografia), si se conoce. */
function levelRank(
  index: SemanticModelIndex,
  dimensionId: string,
): { family: 'calendar' | 'geo'; rank: number } | undefined {
  const resolved = index.resolveDimension(dimensionId);
  if (!resolved) return undefined;
  let semantic: string | undefined = resolved.semanticType;
  if (isCalculatedDimension(resolved.dimension)) {
    const { expression } = resolved.dimension;
    semantic = undefined;
    if (expression.type === 'function') {
      const [grain] = expression.args;
      semantic =
        expression.name === 'date-trunc'
          ? grain?.type === 'literal' && typeof grain.value === 'string'
            ? grain.value
            : undefined
          : expression.name;
    }
  }
  if (semantic === undefined) return undefined;
  const calendar = CALENDAR_RANK[semantic];
  if (calendar !== undefined) return { family: 'calendar', rank: calendar };
  const geo = GEO_RANK[semantic];
  return geo === undefined ? undefined : { family: 'geo', rank: geo };
}
